import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface DialogProps {
  trigger?: React.ReactNode;
  children: React.ReactNode;
  onClose?: () => void;
  className?: string;
  headerClassName?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  subHeader?: string;
  header?: string;
  backdropHandlerDisabled?: boolean;
  outsideClickDisabled?: boolean;
}

export function Dialog({
  trigger,
  children,
  className = "",
  isOpen,
  setIsOpen,
  onClose,
  subHeader,
  header,
  headerClassName,
  backdropHandlerDisabled = false,
  outsideClickDisabled = false,
}: DialogProps) {
  const [mounted, setMounted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [isDark, setIsDark] = useState(false);

  // Detect dark mode using class on html element (Tailwind's default)
  useEffect(() => {
    const checkDark = () => {
      if (typeof window !== "undefined") {
        // Check both <html> and <body> for the "dark" class
        const htmlHasDark = document.documentElement.classList.contains("dark");
        const bodyHasDark = document.body.classList.contains("dark");
        setIsDark(htmlHasDark || bodyHasDark);
      }
    };

    checkDark();

    // Listen for class changes on both <html> and <body>
    const htmlObserver = new MutationObserver(checkDark);
    const bodyObserver = new MutationObserver(checkDark);

    htmlObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      htmlObserver.disconnect();
      bodyObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      if (!outsideClickDisabled) {
        document.addEventListener("mousedown", handleClickOutside);
      }
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      if (!outsideClickDisabled) {
        document.removeEventListener("mousedown", handleClickOutside);
      }
      document.body.style.overflow = "unset";
    };
  }, [ isOpen, outsideClickDisabled]);

  const handleClose = () => {
    setIsClosing(true);
    if (onClose) onClose();
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 300); // Match this with the animation duration
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleTriggerClick = () => {
    setIsOpen(true);
  };

  const triggerElement = trigger
    ? React.cloneElement(trigger as React.ReactElement, {
        onClick: handleTriggerClick,
        style: { cursor: "pointer" },
      })
    : null;
  if (!mounted) return triggerElement;

  // Choose background based on dark mode
  const lightBg = "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)";
  const darkBg = "linear-gradient(135deg, #18181b 0%, #27272a 100%)";

  return (
    <>
      {trigger && triggerElement}
      {(isOpen || isClosing) &&
        createPortal(
          <div
            className="fixed  inset-0 z-50 flex items-center justify-center p-4"
            onClick={backdropHandlerDisabled ? undefined : handleBackdropClick}
          >
            {/* Backdrop */}
            <div
              className={`
              bg-black/60 dark:bg-black/80 fixed inset-0 backdrop-blur-sm
              transition-opacity duration-300 ease-out
              ${isClosing ? "opacity-0" : "opacity-100"}
            `}
            />

            {/* Dialog */}
            <div
              ref={dialogRef}
              className={`
                
              relative max-h-[90vh]  max-w-[80vw]
              transform overflow-auto rounded-2xl   shadow-2xl
              transition-all duration-300 ease-out  
              ${
                isClosing
                  ? "translate-y-4 scale-95 opacity-0"
                  : "translate-y-0 scale-100 opacity-100"
              }
              border border-gray-200/50 p-4
              dark:border-gray-700/70 sm:p-6
              ${className}
            `}
              style={{
                background: isDark ? darkBg : lightBg,
                backgroundImage: isDark ? darkBg : lightBg,
                boxShadow:
                  "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)",
                animation: !isClosing
                  ? "scaleIn 0.3s ease-out"
                  : "scaleOut 0.3s ease-out",
              }}
            >
              {/* Close button */}
              <button
                onClick={handleClose}
                className="
                fixed right-4 top-4 z-10
                flex h-8 w-8
                items-center justify-center
                rounded-full bg-gray-100
                text-gray-500 transition-colors duration-200 hover:bg-gray-200 hover:text-gray-700
                dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200
              "
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>

              {/* Content */}

              <div className="dark:text-gray-100 ">
                {header && (
                  <div
                    className={` mb-4  border-b border-gray-200/60  pb-4 dark:border-gray-700/60 sm:mb-6 ${headerClassName}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 max-w-[90%] flex-1">
                        <h2 className="text-lg font-semibold capitalize leading-tight text-gray-900 dark:text-white sm:text-xl lg:text-2xl">
                          {header}
                        </h2>
                        {subHeader && (
                          <p className="mt-2 max-w-[90%] text-sm leading-relaxed text-gray-600 dark:text-gray-300 sm:text-base">
                            {subHeader}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className={header ? "" : "pt-0"}>{children}</div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

// Add keyframes for scale animations
if (
  typeof window !== "undefined" &&
  !document.getElementById("dialog-scale-keyframes")
) {
  const style = document.createElement("style");
  style.id = "dialog-scale-keyframes";
  style.textContent = `
    @keyframes scaleIn {
      from {
        transform: scale(0.95);
        opacity: 0;
      }
      to {
        transform: scale(1);
        opacity: 1;
      }
    }

    @keyframes scaleOut {
      from {
        transform: scale(1);
        opacity: 1;
      }
      to {
        transform: scale(0.95);
        opacity: 0;
      }
    }
  `;
  document.head.appendChild(style);
}
